import { cn } from '@/lib/cn';
import { useMe, type WcPhase, type WcSlot, type WcTeamRef } from '@/lib/queries';

/** Zona horaria del país del colaborador (cae a Guatemala si no se conoce). */
export const DEFAULT_TZ = 'America/Guatemala';
export function useViewerTimezone(): string {
  const { data } = useMe();
  return data?.employee?.country?.timezone || DEFAULT_TZ;
}

export const PHASE_LABEL: Record<WcPhase, string> = {
  GROUP: 'Fase de grupos',
  R32: 'Dieciseisavos',
  R16: 'Octavos de final',
  QF: 'Cuartos de final',
  SF: 'Semifinal',
  THIRD: 'Tercer lugar',
  FINAL: 'Final',
};

export const PHASE_SHORT: Record<WcPhase, string> = {
  GROUP: 'Grupos',
  R32: '16avos',
  R16: 'Octavos',
  QF: 'Cuartos',
  SF: 'Semis',
  THIRD: '3er lugar',
  FINAL: 'Final',
};

export const PHASE_ORDER: WcPhase[] = ['GROUP', 'R32', 'R16', 'QF', 'SF', 'THIRD', 'FINAL'];

export function slotLabel(slot?: WcSlot | null): string {
  if (!slot) return 'Por definir';
  if (slot.type === 'GROUP_POS') return `${slot.pos}° Grupo ${slot.group}`;
  if (slot.type === 'WINNER') return `Ganador del partido ${slot.ref}`;
  if (slot.type === 'LOSER') return `Perdedor del partido ${slot.ref}`;
  if (slot.type === 'THIRD_SET') return `3° (${slot.groups.join('/')})`;
  return 'Por definir';
}

export function formatKickoff(
  iso: string,
  tz: string = DEFAULT_TZ,
): { date: string; time: string; weekday: string } {
  const d = new Date(iso);
  const date = new Intl.DateTimeFormat('es', {
    timeZone: tz,
    day: '2-digit',
    month: 'short',
  }).format(d);
  const time = new Intl.DateTimeFormat('es', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d);
  const weekday = new Intl.DateTimeFormat('es', { timeZone: tz, weekday: 'long' }).format(d);
  return { date, time, weekday };
}

/** Clave de día (yyyy-mm-dd en la zona del usuario) para agrupar partidos. */
export function dayKey(iso: string, tz: string = DEFAULT_TZ): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

export function dayLabel(iso: string, tz: string = DEFAULT_TZ): string {
  const d = new Date(iso);
  const s = new Intl.DateTimeFormat('es', {
    timeZone: tz,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(d);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Bandera del país por código ISO-2 (assets locales en /flags). */
export function Flag({ iso2, className }: { iso2?: string | null; className?: string }) {
  if (!iso2) {
    return (
      <span
        className={cn(
          'inline-flex items-center justify-center rounded-sm bg-bg text-[9px] font-bold text-font-tertiary ring-1 ring-black/5',
          className,
        )}
      >
        ?
      </span>
    );
  }
  return (
    <img
      src={`/flags/${iso2}.svg`}
      alt=""
      loading="lazy"
      className={cn('inline-block rounded-sm object-cover ring-1 ring-black/10', className)}
    />
  );
}

/** Nombre + bandera de un equipo (o placeholder de slot). */
export function TeamLabel({
  team,
  slot,
  align = 'left',
  flagClass = 'h-5 w-7',
  className,
}: {
  team?: WcTeamRef | null;
  slot?: WcSlot | null;
  align?: 'left' | 'right';
  flagClass?: string;
  className?: string;
}) {
  const reverse = align === 'right';
  return (
    <span
      className={cn(
        'flex min-w-0 items-center gap-2',
        reverse && 'flex-row-reverse text-right',
        className,
      )}
    >
      {team ? (
        <Flag iso2={team.iso2} className={cn('flex-shrink-0', flagClass)} />
      ) : (
        <Flag className={cn('flex-shrink-0', flagClass)} />
      )}
      <span className="min-w-0 truncate font-semibold text-font-primary">
        {team ? team.nameEs : slotLabel(slot)}
      </span>
    </span>
  );
}
